/**
 * HighlightRefNode - メモにハイライトを引用する Tiptap カスタムノード。
 *
 * - 属性は highlightId と本文・出典のスナップショットだけ持ち、表示は取得したハイライトを正とする
 * - highlightId が無い間はハイライトを選ぶダイアログを開く欄を表示する (highlight-ref-node-view.tsx)
 */

import { mergeAttributes, Node } from '@tiptap/core'
import type { NodeViewProps } from '@tiptap/react'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { HighlightRefNodeView, requestHighlightPicker } from './highlight-ref-node-view'

export type HighlightRefAttrs = { highlightId: number; text: string; source: string }

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    highlightRef: {
      /** カーソル位置にハイライトの引用を挿入する。attrs を省略すると選択ダイアログを出す */
      insertHighlightRef: (attrs?: HighlightRefAttrs) => ReturnType
    }
  }
}

export const HighlightRefNode = Node.create({
  name: 'highlightRef',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      highlightId: {
        default: null as number | null,
        parseHTML: (el) => {
          const n = Number(el.getAttribute('data-highlight-id'))
          return Number.isInteger(n) && n > 0 ? n : null
        },
        renderHTML: (attrs) => (attrs.highlightId != null ? { 'data-highlight-id': String(attrs.highlightId) } : {}),
      },
      text: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-text') ?? '',
        renderHTML: (attrs) => ({ 'data-text': String(attrs.text ?? '') }),
      },
      source: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-source') ?? '',
        renderHTML: (attrs) => ({ 'data-source': String(attrs.source ?? '') }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="highlight-ref"]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'highlight-ref' }, HTMLAttributes)]
  },

  // 引用文と出典でメモを検索できるよう content_text に載せる
  renderText({ node }) {
    return [node.attrs.text, node.attrs.source].filter(Boolean).join(' — ')
  },

  addNodeView() {
    return ReactNodeViewRenderer(HighlightRefNodeView as (props: NodeViewProps) => React.JSX.Element)
  },

  addCommands() {
    return {
      insertHighlightRef:
        (attrs) =>
        ({ chain }) => {
          if (!attrs) requestHighlightPicker()
          return chain().insertContent({ type: this.name, attrs }).run()
        },
    }
  },
})
